export type RdoFormOption = {
  id: number;
  label: string;
};

export type RdoTaskBreakdownEntry = {
  taskId: number;
  taskType: string | null;
  description: string | null;
  situation: string | null;
  estimatedTimeHours: number;
  totalQuantity: number;
  actualQuantity: number;
  completionPercentage: number | null;
};

export type RdoScheduleActivity = {
  id: number;
  level: 'task' | 'activity' | string;
  code: string | null;
  title: string;
  tags: string[];
  situation: string | null;
  situationLabel: string;
  activityType: string | null;
  activityTypeLabel: string;
  taskType: string | null;
  taskBreakdown: RdoTaskBreakdownEntry[];
  stage: string | null;
  floor: string | null;
  floorCode?: string | null;
  vertical: string | null;
  system: string | null;
  stageSortOrder?: number | null;
  floorSortOrder?: number | null;
  verticalSortOrder?: number | null;
  systemSortOrder?: number | null;
  sortOrder?: number | null;
  device: string | null;
  plannedStart: string | null;
  plannedFinish: string | null;
  responsibleNames: string[];
  responsibleIds?: number[];
  teamId: number | null;
  estimatedTimeHours: number;
  totalQuantity: number;
  actualQuantity: number;
  completionPercentage: number | null;
  highlight: 'today' | 'overdue' | null;
};

export type RdoActivityOption = RdoScheduleActivity;

export type RdoColaborador = {
  id: string;
  userId: number;
  name: string;
};

export type RdoCreatePrefill = {
  colaboradores: RdoColaborador[];
  activityIds: number[];
};

export type RdoIntercorrencia = {
  id: string;
  origin: 'engetel' | 'cliente';
  type: 'mob' | 'materiais' | 'recursos' | 'intemperie' | 'obra';
  description: string;
  startTime: string;
  endTime: string;
  unproductiveHours: boolean;
  tags: string[];
  timeRange: string;
};

export type RdoPendenciaLinkType =
  | 'activity'
  | 'floor'
  | 'stage'
  | 'system'
  | 'vertical';

export type RdoPendencia = {
  id: string;
  tag: string;
  priority: string;
  description: string;
  scheduleItemId?: number | null;
  linkType?: RdoPendenciaLinkType | null;
  linkLabel?: string | null;
  linkStage?: string | null;
  linkFloor?: string | null;
  linkVertical?: string | null;
  linkSystem?: string | null;
  /** Equipe que deve atender a pendência (opcional). */
  teamId?: number | null;
  teamLabel?: string | null;
};

export type RdoAtividadeSituacao =
  | 'pending'
  | 'in_progress'
  | 'blocked'
  | 'completed';

export type RdoAtividadePhase = {
  scheduleItemId: number;
  taskType: 'cabling' | 'installation' | 'configuration' | string;
  label: string;
  current: number;
  total: number;
  estimatedTimeHours?: number;
};

export type RdoApontamento = {
  scheduleItemId: number;
  taskType: string;
  label: string;
  quantity: number;
  actualQuantity: number;
  totalQuantity: number;
  estimatedTimeHours?: number;
  completionPercentage?: number | null;
};

export type RdoAtividade = {
  id: string;
  scheduleItemId: number;
  stage?: string | null;
  stageSortOrder?: number | null;
  tags: string[];
  title: string;
  status: string;
  situation: RdoAtividadeSituacao;
  activityType: string | null;
  taskType: string | null;
  device: string | null;
  isPendingType: boolean;
  progress: { label: string; current: number; total: number }[];
  phases: RdoAtividadePhase[];
  /** Estado local da UI — não é persistido no payload. */
  apontar: { cabo: number; instal: number; confg: number };
  /** Fonte de verdade persistida no payload do RDO. */
  apontamentos?: RdoApontamento[];
  completionPercentage: number | null;
};

export type RdoEvidencia = {
  id: string;
  scheduleItemId: number;
  activityTitle: string;
  activityTags: string[];
  floorCode?: string | null;
  caption: string;
  dataUrl: string;
  mimeType: string;
  sizeBytes: number;
  storagePath?: string;
  downloadUrl?: string | null;
  /** SHA-256 do conteúdo binário — evita incluir a mesma mídia duas vezes. */
  contentHash?: string;
};

export type RdoFormOptions = {
  clients: RdoFormOption[];
  projects: RdoFormOption[];
  projectMeta?: { id: number; label: string; clientId: number | null }[];
  teams: RdoFormOption[];
  /** Todas as equipes ativas (para designar responsável por pendências). */
  allTeams?: RdoFormOption[];
  teamMembers: RdoFormOption[];
  weatherOptions: string[];
  defaultProjectId?: number | null;
  defaultTeamId?: number | null;
  /** Equipe do usuário logado (cadastro), distinta da equipe do relatório em edição. */
  viewerTeamId?: number | null;
  viewerTeamLabel?: string | null;
  defaultClientId?: number | null;
  activities?: RdoScheduleActivity[];
  prefill?: RdoCreatePrefill | null;
};

export const RDO_SITUATION_OPTIONS: { value: RdoAtividadeSituacao; label: string }[] =
  [
    { value: 'pending', label: 'Pendente' },
    { value: 'in_progress', label: 'Em andamento' },
    { value: 'blocked', label: 'Bloqueada' },
    { value: 'completed', label: 'Concluída' },
  ];

export const RDO_INTERCORRENCIA_ORIGINS = [
  { value: 'engetel' as const, label: 'Engetel' },
  { value: 'cliente' as const, label: 'Cliente' },
];

export const RDO_INTERCORRENCIA_TYPES = [
  { value: 'mob' as const, label: 'MOB' },
  { value: 'materiais' as const, label: 'Materiais' },
  { value: 'recursos' as const, label: 'Recursos' },
  { value: 'intemperie' as const, label: 'Intempérie' },
  { value: 'obra' as const, label: 'Obra' },
];
